import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { GlobalRadioStation } from '@/mocks/globalRadios';
import { categoryIcons, activityLabels, activityColors } from '@/mocks/globalRadios';
import { useAudioPlayer } from '@/context/AudioContext';
import { SwalAlert } from '@/hooks/useSweetAlert';
import { radioStations } from '@/mocks/radios';
import {
  canPlayStream,
  shouldFallbackToRfw,
  isTerminalStatus,
  isTransientStatus,
  technicalStreamStatusShortLabels,
  technicalStreamStatusColors,
  technicalStreamStatusIcons,
} from '@/types/streaming';

interface RadioCardProps {
  radio: GlobalRadioStation;
  viewMode: 'grid' | 'list';
}

export default function RadioCard({ radio, viewMode }: RadioCardProps) {
  const navigate = useNavigate();
  const { isPlaying, currentStation, play, pause } = useAudioPlayer();
  const [isFollowed, setIsFollowed] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);

  const isThisPlaying = isPlaying && currentStation?.id === radio.id;
  const techStatus = radio.technicalStreamStatus;
  const statusColors = technicalStreamStatusColors[techStatus];
  const statusLabel = technicalStreamStatusShortLabels[techStatus];
  const statusIcon = technicalStreamStatusIcons[techStatus];
  const canPlay = canPlayStream(techStatus) || shouldFallbackToRfw(techStatus);
  const isDisabled = isTerminalStatus(techStatus) || isTransientStatus(techStatus);

  const handleEnter = () => {
    navigate(`/radio/${radio.frequency.replace('.', '-')}`);
  };

  const handlePlay = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isThisPlaying) {
      pause();
    } else {
      // Buscar la RadioStation canónica desde radios.ts para pasar todos los campos
      const station = radioStations.find((s) => s.frequency === radio.frequency);
      if (station) {
        play(station);
      } else {
        // Fallback: construir objeto parcial desde GlobalRadioStation
        play({
          id: radio.id,
          name: radio.name,
          slug: radio.slug,
          frequency: radio.frequency,
          city: radio.city,
          state: radio.state,
          country: radio.country,
          category: radio.category,
          listeners: radio.listeners,
          rating: radio.rating,
          isLive: radio.isLive,
          logoUrl: radio.logoUrl,
          description: radio.description,
        } as any);
      }
    }
  }, [isThisPlaying, pause, play, radio]);

  const handleFollow = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFollowed((prev) => !prev);
    if (!isFollowed) {
      SwalAlert.toast(`Siguiendo a ${radio.name}`, 'success');
    }
  }, [isFollowed, radio.name]);

  const handleFavorite = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFavorite((prev) => !prev);
    if (!isFavorite) {
      SwalAlert.toast(`${radio.name} agregada a favoritos`, 'success');
    }
  }, [isFavorite, radio.name]);

  const handleShare = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/radio/${radio.frequency.replace('.', '-')}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: radio.name, text: `Escucha ${radio.name} en RadioFrecuenciaWeb`, url: shareUrl });
      } catch {
        // user cancelled
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      SwalAlert.toast('Enlace copiado al portapapeles', 'success');
    }
  }, [radio.name, radio.frequency]);

  const scoreColor = radio.radioScore >= 90 ? 'text-green-600' : radio.radioScore >= 80 ? 'text-accent-600' : 'text-yellow-600';
  const scoreBg = radio.radioScore >= 90 ? 'bg-green-500' : radio.radioScore >= 80 ? 'bg-accent-500' : 'bg-yellow-500';

  if (viewMode === 'list') {
    return (
      <div
        onClick={handleEnter}
        className="flex items-center gap-4 bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl p-4 transition-all cursor-pointer group"
      >
        {/* Logo */}
        <div className="relative w-14 h-14 rounded-xl overflow-hidden shrink-0 bg-background-200">
          <img src={radio.logoUrl} alt={radio.name} className="w-full h-full object-cover" />
          {canPlayStream(techStatus) && (
            <div className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-red-500 border-2 border-background-50 animate-pulse"></div>
          )}
          {!canPlayStream(techStatus) && !isDisabled && (
            <div className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-amber-400 border-2 border-background-50"></div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="text-sm font-bold text-foreground-950 truncate">{radio.name}</h3>
            <span className="text-[10px] font-bold text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded-md whitespace-nowrap">{radio.frequency}</span>
          </div>
          <p className="text-[11px] text-foreground-500 truncate">{radio.description}</p>
          <div className="flex items-center gap-3 mt-1.5">
            <span className="text-[10px] text-foreground-400 flex items-center gap-1">
              <i className={`${categoryIcons[radio.category] || 'ri-radio-line'} text-[10px]`}></i>
              {radio.category}
            </span>
            <span className="text-[10px] text-foreground-400 flex items-center gap-1">
              <i className="ri-map-pin-line text-[10px]"></i>
              {radio.city}, {radio.state}
            </span>
            <span className="text-[10px] text-foreground-400 flex items-center gap-1">
              <i className="ri-global-line text-[10px]"></i>
              {radio.language}
            </span>
          </div>
        </div>

        {/* Stats */}
        <div className="hidden sm:flex items-center gap-4 shrink-0">
          <div className="text-right">
            <p className="text-sm font-extrabold text-foreground-950">{radio.listeners.toLocaleString()}</p>
            <p className="text-[9px] text-foreground-400 uppercase">Oyentes</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-extrabold text-foreground-950">{radio.followers.toLocaleString()}</p>
            <p className="text-[9px] text-foreground-400 uppercase">Seguidores</p>
          </div>
          <div className={`w-10 h-10 rounded-xl ${scoreBg} flex items-center justify-center`}>
            <span className="text-xs font-extrabold text-background-50">{radio.radioScore}</span>
          </div>
        </div>

        {/* Activity dot */}
        <div className="hidden md:flex items-center gap-1.5 shrink-0">
          <div className={`w-2 h-2 rounded-full ${activityColors[radio.activityLevel]}`}></div>
          <span className="text-[10px] text-foreground-500">{activityLabels[radio.activityLevel]}</span>
        </div>

        {/* Action buttons */}
        <div className="hidden md:flex items-center gap-1 shrink-0">
          <button
            onClick={handlePlay}
            disabled={isDisabled}
            className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all ${
              isDisabled
                ? 'bg-foreground-200 text-foreground-400 cursor-not-allowed'
                : isThisPlaying
                  ? 'bg-accent-500 text-background-50'
                  : 'bg-primary-500 text-background-50 hover:bg-primary-600'
            }`}
            title={isDisabled ? statusLabel : isThisPlaying ? 'Pausar' : 'Escuchar'}
          >
            <i className={`text-xs ${isDisabled ? 'ri-forbid-2-line' : isThisPlaying ? 'ri-pause-fill' : 'ri-play-fill'}`}></i>
          </button>
          <button
            onClick={handleFollow}
            className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all ${
              isFollowed
                ? 'bg-red-50 text-red-500'
                : 'bg-background-100 text-foreground-400 hover:bg-red-50 hover:text-red-400'
            }`}
            title={isFollowed ? 'Dejar de seguir' : 'Seguir'}
          >
            <i className={`text-xs ${isFollowed ? 'ri-heart-fill' : 'ri-heart-line'}`}></i>
          </button>
          <button
            onClick={handleFavorite}
            className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all ${
              isFavorite
                ? 'bg-amber-50 text-amber-500'
                : 'bg-background-100 text-foreground-400 hover:bg-amber-50 hover:text-amber-400'
            }`}
            title={isFavorite ? 'Quitar de favoritos' : 'Agregar a favoritos'}
          >
            <i className={`text-xs ${isFavorite ? 'ri-star-fill' : 'ri-star-line'}`}></i>
          </button>
          <button
            onClick={handleShare}
            className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer bg-background-100 text-foreground-400 hover:bg-background-200 hover:text-foreground-600 transition-all"
            title="Compartir"
          >
            <i className="ri-share-forward-line text-xs"></i>
          </button>
        </div>

        {/* Go arrow */}
        <i className="ri-arrow-right-s-line text-foreground-300 group-hover:text-foreground-600 transition-colors shrink-0 hidden sm:block"></i>
      </div>
    );
  }

  // Grid view
  return (
    <div
      onClick={handleEnter}
      className="bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl overflow-hidden transition-all cursor-pointer group"
    >
      {/* Logo Area */}
      <div className="relative p-5 flex flex-col items-center text-center">
        <div className="relative w-20 h-20 rounded-2xl overflow-hidden bg-background-200 mb-3">
          <img src={radio.logoUrl} alt={radio.name} className="w-full h-full object-cover" />
          {canPlayStream(techStatus) && (
            <div className="absolute top-0 right-0 w-3.5 h-3.5 rounded-full bg-red-500 border-2 border-background-50 animate-pulse"></div>
          )}
          {!canPlayStream(techStatus) && !isDisabled && (
            <div className="absolute top-0 right-0 w-3.5 h-3.5 rounded-full bg-amber-400 border-2 border-background-50"></div>
          )}
        </div>

        {radio.featured && (
          <div className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-accent-500 text-[9px] font-bold text-background-50">TOP</div>
        )}

        <span className="text-[10px] font-bold text-foreground-400 bg-background-100 px-2 py-0.5 rounded-md mb-2">{radio.frequency}</span>
        <h3 className="text-sm font-extrabold text-foreground-950 leading-tight">{radio.name}</h3>

        <div className="flex items-center gap-1 mt-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <i key={star} className={`text-[10px] ${star <= Math.round(radio.rating) ? 'ri-star-fill text-yellow-500' : 'ri-star-line text-foreground-300'}`}></i>
          ))}
          <span className="text-[10px] font-bold text-foreground-500 ml-1">{radio.rating}</span>
        </div>

        <p className="text-[11px] text-foreground-500 leading-relaxed mt-2 line-clamp-2">{radio.description}</p>
      </div>

      {/* Meta bar */}
      <div className="border-t border-background-100 px-4 py-2.5 flex items-center justify-between text-[10px]">
        <span className="text-foreground-400 flex items-center gap-1">
          <i className={`${categoryIcons[radio.category] || 'ri-radio-line'} text-[10px]`}></i>
          {radio.category}
        </span>
        <span className="text-foreground-400 flex items-center gap-1">
          <i className="ri-map-pin-line text-[10px]"></i>
          {radio.city}
        </span>
      </div>

      {/* Stats bar */}
      <div className="border-t border-background-100 px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <i className="ri-headphone-line text-[10px] text-foreground-400"></i>
            <span className="text-[10px] font-bold text-foreground-700">{radio.listeners.toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-1">
            <i className="ri-heart-line text-[10px] text-foreground-400"></i>
            <span className="text-[10px] font-bold text-foreground-700">{radio.followers.toLocaleString()}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="flex items-center gap-1">
            <i className="ri-fire-line text-[10px] text-red-500"></i>
            <span className="text-[10px] font-bold text-foreground-700">{radio.trendingScore}</span>
          </div>
          <div className={`w-7 h-7 rounded-lg ${scoreBg} flex items-center justify-center`}>
            <span className="text-[9px] font-extrabold text-background-50">{radio.radioScore}</span>
          </div>
        </div>
      </div>

      {/* Action buttons bar */}
      <div className="border-t border-background-100 px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <button
            onClick={handlePlay}
            disabled={isDisabled}
            className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all ${
              isDisabled
                ? 'bg-foreground-200 text-foreground-400 cursor-not-allowed'
                : isThisPlaying
                  ? 'bg-accent-500 text-background-50'
                  : 'bg-primary-500 text-background-50 hover:bg-primary-600'
            }`}
            title={isDisabled ? statusLabel : isThisPlaying ? 'Pausar' : 'Escuchar'}
          >
            <i className={`text-xs ${isDisabled ? 'ri-forbid-2-line' : isThisPlaying ? 'ri-pause-fill' : 'ri-play-fill'}`}></i>
          </button>
          <button
            onClick={handleFollow}
            className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all ${
              isFollowed
                ? 'bg-red-50 text-red-500'
                : 'bg-background-100 text-foreground-400 hover:bg-red-50 hover:text-red-400'
            }`}
            title={isFollowed ? 'Dejar de seguir' : 'Seguir'}
          >
            <i className={`text-xs ${isFollowed ? 'ri-heart-fill' : 'ri-heart-line'}`}></i>
          </button>
          <button
            onClick={handleFavorite}
            className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all ${
              isFavorite
                ? 'bg-amber-50 text-amber-500'
                : 'bg-background-100 text-foreground-400 hover:bg-amber-50 hover:text-amber-400'
            }`}
            title={isFavorite ? 'Quitar de favoritos' : 'Agregar a favoritos'}
          >
            <i className={`text-xs ${isFavorite ? 'ri-star-fill' : 'ri-star-line'}`}></i>
          </button>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleShare}
            className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer bg-background-100 text-foreground-400 hover:bg-background-200 hover:text-foreground-600 transition-all"
            title="Compartir"
          >
            <i className="ri-share-forward-line text-xs"></i>
          </button>
          <button
            onClick={handleEnter}
            className="px-3 py-1.5 rounded-lg bg-background-100 text-foreground-600 hover:bg-foreground-100 hover:text-foreground-800 text-[10px] font-semibold cursor-pointer transition-all whitespace-nowrap flex items-center gap-1"
          >
            Entrar
            <i className="ri-arrow-right-line text-[9px]"></i>
          </button>
        </div>
      </div>
    </div>
  );
}